import {createApi} from '@reduxjs/toolkit/query/react'
import {
  IAssignment,
  IAssignmentPaging,
  IAssignmentPagingParameters,
  ICreateAssignmentRequest,
  IUpdateAssignmentRequest,
  IUpdateAssignmentStatusRequest,
  IUpdateJobCompensationRequest,
  IUpdateDirectDepositRequest,
} from '../../models/assignment'
import {baseQuery} from '../base-query'

export const assignmentApi = createApi({
  reducerPath: 'assignmentApi',
  baseQuery,
  tagTypes: ['Assignment'],
  endpoints: (builder) => ({
    // Get assignments with pagination (POST /api/assignment/assignments)
    getAssignments: builder.mutation<IAssignmentPaging, IAssignmentPagingParameters>({
      query: (params) => ({
        url: '/api/assignment/assignments',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Get single assignment by ID
    getAssignmentById: builder.query<IAssignment, string>({
      query: (id) => `/assignment/${id}`,
      providesTags: ['Assignment'],
    }),

    // Create new assignment
    createAssignment: builder.mutation<IAssignment, ICreateAssignmentRequest>({
      query: (body) => ({
        url: '/assignment/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Update assignment
    updateAssignment: builder.mutation<IAssignment, IUpdateAssignmentRequest>({
      query: (body) => ({
        url: '/assignment/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Update assignment status (activate/end/cancel)
    updateAssignmentStatus: builder.mutation<IAssignment, IUpdateAssignmentStatusRequest>({
      query: (body) => ({
        url: '/assignment/updateStatus',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Activate assignment
    activateAssignment: builder.mutation<
      IAssignment,
      {id: string; startDate: string; note?: string}
    >({
      query: (body) => ({
        url: '/assignment/activate',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // End assignment
    endAssignment: builder.mutation<IAssignment, {id: string; endDate: string; note?: string}>({
      query: (body) => ({
        url: '/assignment/end',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Update job compensation
    updateJobCompensation: builder.mutation<IAssignment, IUpdateJobCompensationRequest>({
      query: (body) => ({
        url: '/assignment/updateJobCompensation',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Update direct deposit
    updateAssignmentDirectDeposit: builder.mutation<IAssignment, IUpdateDirectDepositRequest>({
      query: (body) => ({
        url: '/assignment/updateDirectDeposit',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Delete assignment (soft delete)
    deleteAssignment: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assignment/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Validate assignment ID against Ronos (per-card safety check)
    validateRonosAssignment: builder.mutation<
      {canProceed: boolean},
      {applicantId: string; assignmentId: string; employeeId?: string}
    >({
      query: (body) => ({
        url: '/api/assignment/validate-ronos-assignment',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetAssignmentsQuery,
  useGetAssignmentByIdQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useUpdateAssignmentStatusMutation,
  useActivateAssignmentMutation,
  useEndAssignmentMutation,
  useUpdateJobCompensationMutation,
  useUpdateAssignmentDirectDepositMutation,
  useDeleteAssignmentMutation,
  useValidateRonosAssignmentMutation,
} = assignmentApi
